import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import { ArrowRight } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

interface PersonalInfo {
  fullName: string;
  email: string;
  phone: string;
  nationality: string;
  currentCountry: string;
  dateOfBirth: string;
}

interface VisaCategory {
  duration: string;
  purposes: string[];
}

interface Education {
  level: string;
  fieldOfStudy: string;
  yearsExperience: string;
  currentOccupation: string;
  industry: string;
  achievements: string;
}

interface Investment {
  isInvestor: string;
  investmentType: string;
  investmentValue: string;
  monthlyIncome: string;
  netWorth: string;
}

interface SpecializedTalent {
  isTalent: string;
  accreditation: string;
  achievements: string;
  academicAchievements: string;
}

const DubaiGoldenVisaForm = () => {
  const { toast } = useToast();
  const [personalInfo, setPersonalInfo] = useState<PersonalInfo>({
    fullName: "",
    email: "",
    phone: "",
    nationality: "",
    currentCountry: "",
    dateOfBirth: "",
  });

  const [visaCategory, setVisaCategory] = useState<VisaCategory>({
    duration: "",
    purposes: [],
  });

  const [education, setEducation] = useState<Education>({
    level: "",
    fieldOfStudy: "",
    yearsExperience: "",
    currentOccupation: "",
    industry: "",
    achievements: "",
  });

  const [investment, setInvestment] = useState<Investment>({
    isInvestor: "",
    investmentType: "",
    investmentValue: "",
    monthlyIncome: "",
    netWorth: "",
  });

  const [specializedTalent, setSpecializedTalent] = useState<SpecializedTalent>({
    isTalent: "",
    accreditation: "",
    achievements: "",
    academicAchievements: "",
  });

  const handleSubmit = () => {
    // Validate required fields
    if (!personalInfo.fullName || !personalInfo.email || !personalInfo.phone || 
        !personalInfo.nationality || !personalInfo.currentCountry || !personalInfo.dateOfBirth) {
      toast({
        variant: "destructive",
        title: "Missing Information",
        description: "Please fill in all required personal information fields.",
      });
      return;
    }

    if (!visaCategory.duration) {
      toast({
        variant: "destructive",
        title: "Missing Information",
        description: "Please select your preferred visa duration.",
      });
      return;
    }

    // Process form data
    const formData = {
      personalInfo,
      visaCategory,
      education,
      investment,
      specializedTalent,
    };

    console.log("Form submission:", formData);
    
    toast({
      title: "Assessment Submitted",
      description: "Your Dubai Golden Visa assessment has been submitted successfully.",
    });
  };

  return (
    <Card className="w-full max-w-4xl mx-auto">
      <CardHeader>
        <CardTitle>Dubai Golden Visa Eligibility Assessment</CardTitle>
      </CardHeader>
      <CardContent className="space-y-8">
        {/* Personal Information */}
        <div className="space-y-6">
          <h3 className="text-lg font-semibold">1. Personal Information</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-2">
              <Label htmlFor="fullName">Full Name *</Label>
              <Input
                id="fullName"
                value={personalInfo.fullName}
                onChange={(e) => setPersonalInfo(prev => ({ ...prev, fullName: e.target.value }))}
                placeholder="Enter your full name"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="email">Email Address *</Label>
              <Input
                id="email"
                type="email"
                value={personalInfo.email}
                onChange={(e) => setPersonalInfo(prev => ({ ...prev, email: e.target.value }))}
                placeholder="Enter your email"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="phone">Phone Number *</Label>
              <Input
                id="phone"
                value={personalInfo.phone}
                onChange={(e) => setPersonalInfo(prev => ({ ...prev, phone: e.target.value }))}
                placeholder="Enter your phone number"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="nationality">Nationality *</Label>
              <Input
                id="nationality"
                value={personalInfo.nationality}
                onChange={(e) => setPersonalInfo(prev => ({ ...prev, nationality: e.target.value }))}
                placeholder="Enter your nationality"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="currentCountry">Current Country of Residence *</Label>
              <Input
                id="currentCountry"
                value={personalInfo.currentCountry}
                onChange={(e) => setPersonalInfo(prev => ({ ...prev, currentCountry: e.target.value }))}
                placeholder="Enter your current country"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="dateOfBirth">Date of Birth *</Label>
              <Input
                id="dateOfBirth"
                type="date"
                value={personalInfo.dateOfBirth}
                onChange={(e) => setPersonalInfo(prev => ({ ...prev, dateOfBirth: e.target.value }))}
              />
            </div>
          </div>
        </div>

        {/* Visa Category Interest */}
        <div className="space-y-6 border-t pt-6">
          <h3 className="text-lg font-semibold">2. Visa Category Interest</h3>
          
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Which Dubai Golden Visa duration are you interested in? *</Label>
              <RadioGroup
                value={visaCategory.duration}
                onValueChange={(value) => setVisaCategory(prev => ({ ...prev, duration: value }))}
              >
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="10-year" id="duration-10" />
                  <Label htmlFor="duration-10">10-Year Golden Visa</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="5-year" id="duration-5" />
                  <Label htmlFor="duration-5">5-Year Golden Visa</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="not-sure" id="duration-not-sure" />
                  <Label htmlFor="duration-not-sure">Not Sure (Evaluate both)</Label>
                </div>
              </RadioGroup>
            </div>

            <div className="space-y-2">
              <Label>Primary Purpose (select all that apply)</Label>
              <div className="grid gap-2">
                {[
                  "Investor",
                  "Entrepreneur / Startup Founder",
                  "Specialized Talent",
                  "Outstanding Student / Graduate",
                  "Others"
                ].map((purpose) => (
                  <div key={purpose} className="flex items-center space-x-2">
                    <Checkbox
                      id={purpose}
                      checked={visaCategory.purposes.includes(purpose)}
                      onCheckedChange={(checked) => {
                        setVisaCategory(prev => ({
                          ...prev,
                          purposes: checked
                            ? [...prev.purposes, purpose]
                            : prev.purposes.filter(p => p !== purpose)
                        }));
                      }}
                    />
                    <Label htmlFor={purpose}>{purpose}</Label>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Educational & Professional Background */}
        <div className="space-y-6 border-t pt-6">
          <h3 className="text-lg font-semibold">3. Educational & Professional Background</h3>
          
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="education-level">Highest Level of Education *</Label>
              <Select
                value={education.level}
                onValueChange={(value) => setEducation(prev => ({ ...prev, level: value }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select education level" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="high-school">High School</SelectItem>
                  <SelectItem value="bachelors">Bachelor's Degree</SelectItem>
                  <SelectItem value="masters">Master's Degree</SelectItem>
                  <SelectItem value="phd">PhD</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="field-study">Field(s) of Study / Major</Label>
              <Input
                id="field-study"
                value={education.fieldOfStudy}
                onChange={(e) => setEducation(prev => ({ ...prev, fieldOfStudy: e.target.value }))}
                placeholder="Enter your field of study"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="years-experience">Years of Professional Experience *</Label>
              <Input
                id="years-experience"
                type="number"
                value={education.yearsExperience}
                onChange={(e) => setEducation(prev => ({ ...prev, yearsExperience: e.target.value }))}
                placeholder="Enter years of experience"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="achievements">Notable Achievements or Recognitions</Label>
              <Textarea
                id="achievements"
                value={education.achievements}
                onChange={(e) => setEducation(prev => ({ ...prev, achievements: e.target.value }))}
                placeholder="List any major awards, publications, patents, or leadership roles"
                className="min-h-[100px]"
              />
            </div>
          </div>
        </div>

        {/* Investment & Financial Criteria */}
        <div className="space-y-6 border-t pt-6">
          <h3 className="text-lg font-semibold">4. Investment & Financial Criteria</h3>
          
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Are you an Investor or Property Owner in the UAE?</Label>
              <RadioGroup
                value={investment.isInvestor}
                onValueChange={(value) => setInvestment(prev => ({ ...prev, isInvestor: value }))}
              >
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="yes" id="investor-yes" />
                  <Label htmlFor="investor-yes">Yes</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="no" id="investor-no" />
                  <Label htmlFor="investor-no">No</Label>
                </div>
              </RadioGroup>
            </div>

            {investment.isInvestor === "yes" && (
              <>
                <div className="space-y-2">
                  <Label htmlFor="investment-type">Type of Investment</Label>
                  <Select
                    value={investment.investmentType}
                    onValueChange={(value) => setInvestment(prev => ({ ...prev, investmentType: value }))}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select investment type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="real-estate">Real Estate (Property)</SelectItem>
                      <SelectItem value="startup">Startup / Company Investment</SelectItem>
                      <SelectItem value="bonds">Government Bonds</SelectItem>
                      <SelectItem value="other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="investment-value">Investment Value (AED)</Label>
                  <Input
                    id="investment-value"
                    value={investment.investmentValue}
                    onChange={(e) => setInvestment(prev => ({ ...prev, investmentValue: e.target.value }))}
                    placeholder="Enter investment value"
                  />
                </div>
              </>
            )}

            <div className="space-y-2">
              <Label htmlFor="monthly-income">Monthly Income (AED)</Label>
              <Input
                id="monthly-income"
                value={investment.monthlyIncome}
                onChange={(e) => setInvestment(prev => ({ ...prev, monthlyIncome: e.target.value }))}
                placeholder="Enter monthly income"
              />
            </div>
          </div>
        </div>

        {/* Specialized Talent / Outstanding Student Criteria */}
        <div className="space-y-6 border-t pt-6">
          <h3 className="text-lg font-semibold">5. Specialized Talent / Outstanding Student Criteria</h3>
          
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Are you recognized as a Specialized Talent, Scientist, Creative Professional, or Medical Professional?</Label>
              <RadioGroup
                value={specializedTalent.isTalent}
                onValueChange={(value) => setSpecializedTalent(prev => ({ ...prev, isTalent: value }))}
              >
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="yes" id="talent-yes" />
                  <Label htmlFor="talent-yes">Yes</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="no" id="talent-no" />
                  <Label htmlFor="talent-no">No</Label>
                </div>
              </RadioGroup>
            </div>

            {specializedTalent.isTalent === "yes" && (
              <div className="space-y-2">
                <Label htmlFor="accreditation">Accreditation Details</Label>
                <Textarea
                  id="accreditation"
                  value={specializedTalent.accreditation}
                  onChange={(e) => setSpecializedTalent(prev => ({ ...prev, accreditation: e.target.value }))}
                  placeholder="Provide details of your accreditation or endorsements"
                  className="min-h-[100px]"
                />
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="academic-achievements">Academic or Research Achievements</Label>
              <Textarea
                id="academic-achievements"
                value={specializedTalent.academicAchievements}
                onChange={(e) => setSpecializedTalent(prev => ({ ...prev, academicAchievements: e.target.value }))}
                placeholder="List notable publications, patents, grants, or research contributions"
                className="min-h-[100px]"
              />
            </div>
          </div>
        </div>

        {/* Submit Button */}
        <Button 
          onClick={handleSubmit}
          className="w-full"
          size="lg"
        >
          Submit Assessment
          <ArrowRight className="ml-2 h-5 w-5" />
        </Button>
      </CardContent>
    </Card>
  );
};

export default DubaiGoldenVisaForm;